import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  PLATFORM_ID,
  TemplateRef,
  computed,
  contentChild,
  inject,
  input,
  model,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { InfoTooltipComponent } from '../info-tooltip/info-tooltip.component';
import {
  ChartArtistClickEvent,
  ChartArtistLink,
  ChartColumnDef,
  ChartMovementInfo,
  ChartRowClickEvent,
} from './chart-table.types';

@Component({
  selector: 'app-chart-table',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, InfoTooltipComponent],
  templateUrl: './chart-table.component.html',
  host: {
    class: 'w-full min-w-0 flex-1 min-h-0 flex flex-col justify-between overflow-hidden',
  },
  styles: `
    :host {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      flex: 1 1 0%;
      min-width: 0;
      min-height: 0;
      width: 100%;
      overflow: hidden;
    }
  `,
})
export class ChartTableComponent<T = any> implements AfterViewInit, OnDestroy {
  private platformId = inject(PLATFORM_ID);
  private cdr = inject(ChangeDetectorRef);
  private elementRef = inject(ElementRef);

  contentCustomCellTemplate = contentChild<TemplateRef<any>>('customCell');
  customCellTemplate = input<TemplateRef<any> | null>(null);

  readonly effectiveCustomCellTemplate = computed(() => {
    return this.customCellTemplate() ?? this.contentCustomCellTemplate() ?? null;
  });

  tableContainer = viewChild<ElementRef<HTMLDivElement>>('tableContainer');
  tableContainerHeight = signal<number>(0);
  private resizeObserver: ResizeObserver | null = null;

  // Inputs
  data = input<T[]>([]);
  columns = input<ChartColumnDef<T>[]>([]);
  loading = input<boolean>(false);
  emptyMessage = input<string>('No chart data available for this week.');
  minTableWidth = input<string>('100%');
  itemCountLabel = input<string>('Songs');
  sortKey = input<string | null>(null);
  sortDirection = input<'asc' | 'desc'>('asc');

  // Pagination inputs / models
  currentPage = model<number>(1);
  pageSize = model<number>(50);
  pageSizeOptions = input<number[]>([25, 50, 100, 200]);
  showPageSizeSelector = input<boolean>(true);
  pageSizeDropdownOpen = signal<boolean>(false);
  autoPageSize = input<boolean>(false);

  // Outputs
  sortChange = output<string>();
  rowClick = output<ChartRowClickEvent<T>>();
  artistClick = output<ChartArtistClickEvent>();
  pageChange = output<number>();

  // Computed properties
  filteredSongsCount = computed(() => this.data().length);

  readonly effectivePageSizeOptions = computed(() => {
    const rawOptions = this.pageSizeOptions();
    const total = this.filteredSongsCount();
    if (total <= 0) return rawOptions;

    const maxTier = rawOptions.length > 0 ? Math.max(...rawOptions) : 200;

    // If total reaches or exceeds the highest tier (e.g. 200), keep all standard tiers
    if (total >= maxTier) {
      return [...rawOptions];
    }

    // Keep all standard tiers strictly less than total
    const result: number[] = [];
    for (const opt of rawOptions) {
      if (opt < total) {
        result.push(opt);
      }
    }

    // Add the exact total as the final option (e.g. 66 on Shazam, 95 on top-95)
    if (result.length === 0 || result[result.length - 1] !== total) {
      result.push(total);
    }

    return result;
  });

  readonly effectivePageSize = computed(() => {
    const size = this.pageSize();
    const options = this.effectivePageSizeOptions();
    if (options.length === 0) return size;
    const maxOpt = options[options.length - 1];
    if (size > maxOpt) {
      return maxOpt;
    }
    return size;
  });

  totalPages = computed(() => {
    const count = this.filteredSongsCount();
    return Math.max(1, Math.ceil(count / this.effectivePageSize()));
  });

  pageStartItem = computed(() => {
    const total = this.filteredSongsCount();
    if (total === 0) return 0;
    const page = Math.min(Math.max(1, this.currentPage()), this.totalPages());
    return (page - 1) * this.effectivePageSize() + 1;
  });

  pageEndItem = computed(() => {
    const total = this.filteredSongsCount();
    const page = Math.min(Math.max(1, this.currentPage()), this.totalPages());
    return Math.min(page * this.effectivePageSize(), total);
  });

  pagedChartRows = computed(() => {
    const rows = this.data();
    const page = Math.min(Math.max(1, this.currentPage()), this.totalPages());
    const start = (page - 1) * this.effectivePageSize();
    return rows.slice(start, start + this.effectivePageSize());
  });

  calculatedRowHeight = computed(() => {
    const containerH = this.tableContainerHeight();
    const size = this.effectivePageSize();
    if (containerH <= 0 || size <= 0) return null;
    const headerHeight = 44;
    const availableH = Math.max(0, containerH - headerHeight);
    return availableH / size;
  });

  isFullPage = computed(() => {
    const count = this.pagedChartRows().length;
    return count > 0 && count === this.effectivePageSize();
  });

  readonly skeletonRows = computed(() => {
    const count = Math.min(this.effectivePageSize(), 15);
    return Array.from({ length: count }, (_, i) => i + 1);
  });

  ngAfterViewInit(): void {
    if (isPlatformBrowser(this.platformId) && this.autoPageSize()) {
      this.setupAutoPageSize();
      setTimeout(() => this.setupAutoPageSize(), 100);
      setTimeout(() => this.setupAutoPageSize(), 300);
    }
  }

  ngOnDestroy(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    const containerEl = this.tableContainer()?.nativeElement;
    if (containerEl && containerEl.clientHeight > 0) {
      this.updatePageSizeFromHeight(containerEl.clientHeight);
    }
  }

  private setupAutoPageSize(): void {
    const containerEl = this.tableContainer()?.nativeElement;
    if (!containerEl) return;

    this.resizeObserver?.disconnect();

    this.resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const height = entry.contentRect.height;
        if (height > 0) {
          this.updatePageSizeFromHeight(height);
        }
      }
    });

    this.resizeObserver.observe(containerEl);

    if (containerEl.clientHeight > 0) {
      this.updatePageSizeFromHeight(containerEl.clientHeight);
    }
  }

  private updatePageSizeFromHeight(containerHeight: number): void {
    this.tableContainerHeight.set(containerHeight);
    if (!this.autoPageSize()) {
      this.cdr.markForCheck();
      return;
    }

    const headerHeight = 44;
    const rowHeight = 53;
    const safetyBuffer = 10;
    const availableHeight = Math.max(0, containerHeight - headerHeight - safetyBuffer);
    const computedPageSize = Math.max(1, Math.floor(availableHeight / rowHeight));

    if (computedPageSize !== this.pageSize()) {
      this.pageSize.set(computedPageSize);
      const maxPage = this.totalPages();
      if (this.currentPage() > maxPage) {
        this.currentPage.set(maxPage);
      }
      this.cdr.markForCheck();
    }
  }

  togglePageSizeDropdown(event: MouseEvent): void {
    event.stopPropagation();
    this.pageSizeDropdownOpen.update((open) => !open);
  }

  closePageSizeDropdown(): void {
    if (this.pageSizeDropdownOpen()) {
      this.pageSizeDropdownOpen.set(false);
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.closePageSizeDropdown();
    }
  }

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    this.closePageSizeDropdown();
  }

  setPageSize(size: number): void {
    this.closePageSizeDropdown();
    if (this.pageSize() === size) return;
    const oldSize = this.pageSize();
    this.pageSize.set(size);
    // Keep user around the same relative item position
    const currentFirstItem = (this.currentPage() - 1) * oldSize + 1;
    const newPage = Math.max(1, Math.min(Math.ceil(currentFirstItem / size), this.totalPages()));
    if (newPage !== this.currentPage()) {
      this.currentPage.set(newPage);
      this.pageChange.emit(newPage);
    }
  }

  toggleSort(key: string): void {
    this.sortChange.emit(key);
  }

  goToPage(page: number): void {
    const maxPage = this.totalPages();
    const target = Math.max(1, Math.min(page, maxPage));
    this.currentPage.set(target);
    this.pageChange.emit(target);
  }

  prevPage(): void {
    if (this.currentPage() > 1) {
      const prev = this.currentPage() - 1;
      this.currentPage.set(prev);
      this.pageChange.emit(prev);
    }
  }

  nextPage(): void {
    if (this.currentPage() < this.totalPages()) {
      const next = this.currentPage() + 1;
      this.currentPage.set(next);
      this.pageChange.emit(next);
    }
  }

  onGoToInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const val = parseInt(input.value, 10);
    if (isNaN(val)) {
      input.value = this.currentPage().toString();
      return;
    }
    this.goToPage(val);
    input.value = this.currentPage().toString();
  }

  onRowClick(item: T, event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (
      target.closest('button') ||
      target.closest('a') ||
      target.tagName === 'A' ||
      target.tagName === 'BUTTON'
    ) {
      return;
    }
    this.rowClick.emit({ item, event });
  }

  onArtistClick(artist: ChartArtistLink, event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.artistClick.emit({
      id: artist.id,
      slug: artist.slug,
      name: artist.name,
      event,
    });
  }

  getRank(item: any, col: ChartColumnDef<T>): number | string {
    if (col.rankFn) {
      return col.rankFn(item) ?? '–';
    }
    return item?.rank ?? item?.song?.rank ?? '–';
  }

  getMovement(item: any, col: ChartColumnDef<T>): ChartMovementInfo | null {
    if (col.movementFn) {
      return col.movementFn(item);
    }
    if (item?.movement) {
      return item.movement;
    }
    return null;
  }

  getTitle(item: any, col: ChartColumnDef<T>): string {
    if (col.field) {
      if (typeof col.field === 'function') {
        return col.field(item);
      }
      return item?.[col.field] ?? '';
    }
    return item?.title ?? item?.song?.title ?? '';
  }

  getArtists(item: any, col: ChartColumnDef<T>): ChartArtistLink[] {
    if (col.artistsFn) {
      return col.artistsFn(item);
    }
    if (Array.isArray(item?.artists)) {
      return item.artists;
    }
    if (Array.isArray(item?.song?.artists)) {
      return item.song.artists;
    }
    if (typeof item?.artist === 'string') {
      return [{ name: item.artist }];
    }
    if (typeof item?.song?.artist === 'string') {
      return [{ name: item.song.artist }];
    }
    return [];
  }

  getCellValue(item: any, col: ChartColumnDef<T>): any {
    let val: any;
    if (col.field) {
      if (typeof col.field === 'function') {
        val = col.field(item);
      } else {
        val = item?.[col.field] ?? item?.song?.[col.field];
      }
    } else {
      val = item?.[col.id] ?? item?.song?.[col.id];
    }

    if (col.formatter) {
      return col.formatter(val, item);
    }
    return val ?? '–';
  }

  getSkeletonClasses(col: ChartColumnDef<T>): string {
    if (col.skeletonClass) {
      return col.skeletonClass;
    }
    const alignPrefix =
      col.align === 'center' ? 'mx-auto ' : col.align === 'right' ? 'ml-auto ' : '';

    switch (col.type) {
      case 'rank':
        return 'h-5 w-10 rounded bg-card-bg-hover';
      case 'title':
        return 'h-5 w-36 rounded bg-card-bg-hover';
      case 'artists':
        return 'h-5 w-28 rounded bg-card-bg-hover';
      case 'number':
        return `${alignPrefix}h-5 w-8 rounded bg-card-bg-hover`;
      default:
        return `${alignPrefix}h-5 w-16 rounded bg-card-bg-hover`;
    }
  }
}
